/**
 * Instancia única de axios — patrón estándar Sistemify (AJJeans/GGv2/PartnersV2).
 * Interceptor de request agrega `Authorization: Bearer <token>` + `X-Correlation-Id`
 * (UUID por request, para trazar contra los logs del backend). Interceptor de
 * response: en 401 limpia sesión y redirige a `/login`; desempaqueta
 * `{success:false, error:{code,message,details}}` del backend a un `Error`
 * enriquecido.
 */
import axios from 'axios';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  withCredentials: false,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('app_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers['X-Correlation-Id'] = crypto.randomUUID();
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Deja pasar las cancelaciones (AbortController) sin envolver: si no, se pierde
    // `code`/`name` y los callers ya no pueden detectar con `axios.isCancel(err)` que
    // fue un abort intencional (búsqueda server-side superada por una más reciente,
    // ver `hooks/useListado.js`) en vez de un error real.
    if (axios.isCancel(error)) return Promise.reject(error);

    if (error.response?.status === 401) {
      localStorage.removeItem('app_token');
      if (window.location.pathname !== '/login') window.location.href = '/login';
    }

    const backendError = error.response?.data?.error;
    const mensaje = backendError?.message || error.message || 'Error de conexión';
    const enriquecido = new Error(mensaje);
    enriquecido.status = error.response?.status;
    enriquecido.code = backendError?.code;
    enriquecido.details = backendError?.details;
    enriquecido.response = error.response;
    return Promise.reject(enriquecido);
  }
);

// Helpers que desempaquetan {success,data} y regresan directo el payload.
export const get = (url, config) => apiClient.get(url, config).then((r) => r.data.data);
export const getPaginated = (url, config) => apiClient.get(url, config).then((r) => r.data);
export const post = (url, body, config) => apiClient.post(url, body, config).then((r) => r.data.data);
export const put = (url, body, config) => apiClient.put(url, body, config).then((r) => r.data.data);
export const patch = (url, body, config) => apiClient.patch(url, body, config).then((r) => r.data.data);
export const del = (url, config) => apiClient.delete(url, config).then((r) => r.data.data);

export default apiClient;
